/**
 * Secondary Navigation Component
 *
 * scroll-spy functionality to highlight the current section.
 * Includes both mobile (collapsible) and desktop layouts.
 *
 * @component SecondaryNav
 * @extends HTMLElement
 */
function doSecondaryNav() {
  if (customElements.get('secondary-nav')) return

  class SecondaryNav extends HTMLElement {
    constructor() {
      super()
      this.sections = []
      this.navItems = []
      this.header = document.querySelector('.section-header')
      this.announcementBar = document.querySelector('.announcement-bar-section')
      this.sectionElement = this.closest('.secondary-nav')
      this.updatePosition = this.updatePosition.bind(this)
      this.jumpNavTrigger = this.querySelector('.jump-nav__trigger')
      this.init()
    }

    init() {
      // Get all nav items and their corresponding sections
      this.navItems = Array.from(this.querySelectorAll('.jump-nav__item'))
      this.hideNavItemIfSectionDoesNotExist(
        Array.from(this.querySelectorAll('.jump-nav__item'))
      )
      this.navLinksDesktop = Array.from(
        this.querySelectorAll('.jump-nav__menu-desktop .jump-nav__link')
      )
      this.navLinksMobile = Array.from(
        this.querySelectorAll('.jump-nav__menu .jump-nav__link')
      )

      // Ensure first mobile nav link is active on load if none are
      if (
        this.navLinksMobile.length > 0 &&
        !this.navLinksMobile.some((link) => link.classList.contains('active'))
      ) {
        this.navLinksMobile[0].classList.add('active')
      }

      this.sections = this.navLinksDesktop
        .map((item) => {
          const href = item.getAttribute('href')
          if (href.startsWith('#')) {
            const sectionId = href.substring(1)
            return document.getElementById(sectionId)
          }
          return null
        })
        .filter(Boolean)

      this.scrollSpy(this.sections, this.navLinksDesktop)
      this.scrollSpy(this.sections, this.navLinksMobile)

      // Helper for robust scroll to top
      const scrollToTopLoop = () => {
        let attempts = 0
        function loop() {
          window.scrollTo({ top: 0, behavior: 'smooth' })
          attempts++
          if (window.scrollY > 0 && attempts < 10) {
            requestAnimationFrame(loop)
          }
        }
        loop()
      }

      // Intercept anchor clicks for smooth scroll with offset
      this.querySelectorAll('.jump-nav__link[href^="#"]').forEach((link) => {
        link.addEventListener('click', (e) => {
          const href = link.getAttribute('href')
          if (!href || href.length < 2) return
          const targetId = href.substring(1)
          const target = document.getElementById(targetId)
          if (!target) return
          e.preventDefault()

          // Check if parent li has data-top-top='true'
          const parentItem = link.closest('.jump-nav__item')
          if (
            parentItem &&
            parentItem.getAttribute('data-top-top') === 'true'
          ) {
            scrollToTopLoop()
            return
          }

          // Calculate offset: announcement bar + secondary nav
          let offset = 0
          if (this.announcementBar && this.announcementBar.offsetHeight > 0) {
            offset += this.announcementBar.offsetHeight
          }
          if (this.sectionElement && this.sectionElement.offsetHeight > 0) {
            offset += this.sectionElement.offsetHeight
          }

          // Get the top position of the target
          const targetRect = target.getBoundingClientRect()
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop
          const targetTop = targetRect.top + scrollTop - offset

          window.scrollTo({
            top: targetTop,
            behavior: 'smooth',
          })

          this.jumpNavTrigger.click()
        })
      })

      // Keep position in sync with the header/announcement bar size and the header's show/hide state
      window.addEventListener('resize', this.updatePosition)
      this.resizeObserver = new ResizeObserver(this.updatePosition)
      if (this.announcementBar) this.resizeObserver.observe(this.announcementBar)
      if (this.header) {
        this.resizeObserver.observe(this.header)
        this.headerClassObserver = new MutationObserver(this.updatePosition)
        this.headerClassObserver.observe(this.header, {
          attributes: true,
          attributeFilter: ['class'],
        })
      }

      // Keep the next section's offset in sync if the nav's height changes (font load, breakpoint)
      if (this.sectionElement) {
        this.navResizeObserver = new ResizeObserver(() => this.setNavHeight())
        this.navResizeObserver.observe(this.sectionElement)
      }

      // Bind scroll event
      this.onScroll = this.onScroll.bind(this)
      window.addEventListener('scroll', this.onScroll, { passive: true })

      // Initial positioning
      this.updatePosition()

      // Initial check
      this.onScroll()
    }

    hideNavItemIfSectionDoesNotExist(navItems) {
      navItems.forEach((item) => {
        const customScrollToId = item.dataset.customSectionId
        const customScrollToIdElement =
          document.getElementById(customScrollToId)
        if (!customScrollToIdElement) {
          item.style.display = 'none'
        }
      })
    }

    setNavHeight() {
      // Initial value is set inline in secondary-nav.liquid; CSS applies it to the next section
      document.documentElement.style.setProperty(
        '--secondary-nav-height',
        `${this.sectionElement.offsetHeight}px`
      )
    }

    updatePosition() {
      if (!this.sectionElement) return

      let announcementBarHeight = 0
      if (this.announcementBar && this.announcementBar.offsetHeight > 0) {
        announcementBarHeight = this.announcementBar.offsetHeight
      }

      // Only leave room for the header while it's actually shown: either in its default
      // position at the top of the page, or as the revealed sticky header once scrolled past.
      // Reading its classes (rather than guessing from scroll direction) keeps the two in sync,
      // including on load when the page is restored part-way down.
      let headerHeight = 0
      if (this.header) {
        const classes = this.header.classList
        const headerIsShown =
          !classes.contains('shopify-section-header-hidden') &&
          (classes.contains('shopify-section-header-sticky') ||
            !classes.contains('scrolled-past-header'))
        if (headerIsShown) headerHeight = this.header.offsetHeight
      }

      // Tuck 1px under whatever is above (offsetHeight rounds, which can leave a hairline gap).
      // Keep in sync with the CSS default in secondary-nav.scss
      const offset = announcementBarHeight + headerHeight
      this.sectionElement.style.top = `${Math.max(0, offset - 1)}px`
    }

    onScroll() {
      const viewportHeight = window.innerHeight

      // Update the positioning
      this.updatePosition()

      // Add or remove 'is-scrolled' class based on scroll position
      if (window.scrollY > 0) {
        this.sectionElement.classList.add('is-scrolled')
      } else {
        this.sectionElement.classList.remove('is-scrolled')
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll)
      window.removeEventListener('resize', this.updatePosition)
      if (this.resizeObserver) this.resizeObserver.disconnect()
      if (this.headerClassObserver) this.headerClassObserver.disconnect()
      if (this.navResizeObserver) this.navResizeObserver.disconnect()
    }

    scrollSpy(sections, menu_links) {
      const makeActive = (link) => menu_links[link].classList.add('active')
      const removeActive = (link) => menu_links[link].classList.remove('active')
      const removeAllActive = () =>
        [...Array(sections.length).keys()].forEach((link) => removeActive(link))
      const sectionMargin = 200
      let currentActive = 0

      // Extracted logic to set active nav item
      const updateActive = () => {
        const current =
          sections.length -
          [...sections]
            .reverse()
            .findIndex(
              (section) => window.scrollY >= section.offsetTop - sectionMargin
            ) -
          1
        if (
          current !== currentActive &&
          current >= 0 &&
          current < sections.length
        ) {
          removeAllActive()
          currentActive = current
          makeActive(current)
        }
      }

      window.addEventListener('scroll', updateActive)
      // Call once on initialization to set the correct active state
      updateActive()
    }
  }

  customElements.define('secondary-nav', SecondaryNav)
}

doSecondaryNav()
